<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { vOnClickOutside } from '@vueuse/components';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { HEADER_ACTION_BUTTON_CLASS } from 'dashboard/helper/conversationHeaderStyles';
import { getUserPermissions } from 'dashboard/helper/permissionsHelper';

// Permanent Teletel Flows control in the conversation header (owner
// directive): always present for the latest run lifecycle - start, pause,
// resume, stop, details and provably-safe retry. The server is the only
// authorization boundary; this component merely mirrors it.
const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

// The Teletel APIs serialize ISO-8601 timestamps (dynamicTime expects unix).
const formatTime = value => (value ? new Date(value).toLocaleString() : '');

const store = useStore();
const { t, te } = useI18n();

const busy = ref(false);
const selectorOpen = ref(false);
const confirmFlow = ref(null);
const detailsOpen = ref(false);
// Owner directive 2026-09-14: in the reply bar the run is one icon button;
// the status and every action live in a small popover above it.
const menuOpen = ref(false);
const details = ref(null);
const search = ref('');
// One operation UUID per start operation, generated when the confirmation
// opens and reused across duplicate clicks and network retries.
const operationUuid = ref(null);

const run = computed(() =>
  store.getters['teletelFlows/getConversationRun'](props.conversationId)
);
const availableFlows = computed(
  () => store.getters['teletelFlows/getAvailableFlows']
);
const filteredFlows = computed(() =>
  availableFlows.value.filter(f =>
    f.name.toLowerCase().includes(search.value.trim().toLowerCase())
  )
);

const permissions = computed(() =>
  getUserPermissions(
    store.getters.getCurrentUser,
    store.getters.getCurrentAccountId
  )
);
// administrator is an account ROLE, not a permission (owner correction):
// real admin accounts commonly carry role 'administrator' with permissions [].
const isAdministrator = computed(
  () => store.getters.getCurrentRole === 'administrator'
);
const canAct = computed(
  () => isAdministrator.value || permissions.value.includes('flow_trigger')
);

const refresh = () =>
  store.dispatch('teletelFlows/fetchConversationRun', props.conversationId);

watch(
  () => props.conversationId,
  id => {
    if (id) refresh();
  },
  { immediate: true }
);

const statusLabel = computed(() =>
  run.value ? t(`TELETEL_FLOWS.STATUS.${run.value.status}`) : ''
);
const isActiveRun = computed(() =>
  ['running', 'waiting'].includes(run.value?.status)
);
const isTerminal = computed(() =>
  ['finished', 'stopped', 'failed'].includes(run.value?.status)
);
const statusDotClass = computed(() => {
  const map = {
    running: 'bg-n-teal-9',
    waiting: 'bg-n-teal-9',
    paused: 'bg-n-amber-9',
    failed: 'bg-n-ruby-9',
    finished: 'bg-n-slate-9',
    stopped: 'bg-n-slate-9',
  };
  return map[run.value?.status] || 'bg-n-slate-9';
});
// One row style for every popover action: same size as the reply-bar text.
const MENU_ITEM_CLASS =
  'flex items-center gap-2 w-full px-3 py-1.5 text-xs text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-50 disabled:cursor-not-allowed text-start';

const closeMenu = () => {
  menuOpen.value = false;
};

const guard = async fn => {
  if (busy.value) return;
  busy.value = true;
  try {
    await fn();
  } catch (error) {
    // Precise refusal codes from the server translate to actionable text.
    const code = error?.response?.data?.code;
    const codeKey = `TELETEL_FLOWS.CONVERSATION_BANNER.START_ERRORS.${code}`;
    useAlert(
      (code && te(codeKey) && t(codeKey)) ||
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('TELETEL_FLOWS.CONVERSATION_BANNER.ACTION_FAILED')
    );
  } finally {
    busy.value = false;
  }
};

const act = action =>
  guard(async () => {
    menuOpen.value = false;
    await store.dispatch(`teletelFlows/${action}`, {
      runId: run.value.id,
      conversationId: props.conversationId,
    });
    await refresh();
  });

const pauseRun = () => act('pauseRun');
const resumeRun = () => act('resumeRun');
const retryRun = () => act('retryRun');
const stopRun = () => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('TELETEL_FLOWS.CONVERSATION_BANNER.STOP_CONFIRM')))
    return;
  act('stopRun');
};

const openSelector = () =>
  guard(async () => {
    menuOpen.value = false;
    await store.dispatch('teletelFlows/fetchAvailableFlows');
    search.value = '';
    selectorOpen.value = true;
  });

const pickFlow = flow => {
  confirmFlow.value = flow;
  operationUuid.value = window.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  selectorOpen.value = false;
};

const confirmStart = () =>
  guard(async () => {
    await store.dispatch('teletelFlows/startFlow', {
      flowId: confirmFlow.value.id,
      conversationDisplayId: props.conversationId,
      operationUuid: operationUuid.value,
    });
    confirmFlow.value = null;
    operationUuid.value = null;
    await refresh();
  });

const openDetails = () =>
  guard(async () => {
    menuOpen.value = false;
    details.value = await store.dispatch('teletelFlows/fetchRun', run.value.id);
    detailsOpen.value = true;
  });
</script>

<template>
  <div class="flex items-center gap-2 text-xs">
    <!-- no run: the control is permanent - unauthorized users see it
         disabled with a permission tooltip; the server stays the boundary -->
    <span
      v-if="!run"
      class="inline-flex"
      :title="
        canAct ? '' : t('TELETEL_FLOWS.CONVERSATION_BANNER.NO_PERMISSION')
      "
    >
      <NextButton
        ghost
        slate
        sm
        :class="HEADER_ACTION_BUTTON_CLASS"
        icon="i-lucide-play"
        :title="t('TELETEL_FLOWS.CONVERSATION_BANNER.START')"
        :aria-label="t('TELETEL_FLOWS.CONVERSATION_BANNER.START')"
        :disabled="busy || !canAct"
        @click="openSelector"
      />
    </span>

    <!-- any run: one icon button; status + actions in a popover -->
    <div v-if="run" v-on-click-outside="closeMenu" class="relative">
      <NextButton
        ghost
        slate
        sm
        :class="[HEADER_ACTION_BUTTON_CLASS, { 'bg-n-alpha-2': menuOpen }]"
        icon="i-lucide-workflow"
        :title="`${run.flow_name || t('TELETEL_FLOWS.CONVERSATION_BANNER.LABEL')}: ${statusLabel}`"
        :aria-label="`${run.flow_name || t('TELETEL_FLOWS.CONVERSATION_BANNER.LABEL')}: ${statusLabel}`"
        @click="menuOpen = !menuOpen"
      />
      <span
        class="absolute top-1 ltr:right-1 rtl:left-1 size-2 rounded-full pointer-events-none"
        :class="statusDotClass"
        aria-hidden="true"
      />
      <div
        v-if="menuOpen"
        class="absolute bottom-full mb-2 ltr:left-0 rtl:right-0 z-40 w-56 py-1 rounded-lg bg-n-solid-1 border border-n-weak shadow-md flex flex-col text-xs"
      >
        <div
          class="flex items-center gap-2 px-3 py-1.5 border-b border-n-weak text-n-slate-12"
        >
          <span class="size-2 rounded-full shrink-0" :class="statusDotClass" />
          <span class="truncate font-medium">
            {{ run.flow_name || t('TELETEL_FLOWS.CONVERSATION_BANNER.LABEL') }}
          </span>
          <span class="shrink-0 text-n-slate-11 ltr:ml-auto rtl:mr-auto">
            {{ statusLabel }}
          </span>
        </div>
        <template v-if="canAct">
          <button
            v-if="isActiveRun"
            type="button"
            :class="MENU_ITEM_CLASS"
            :aria-label="t('TELETEL_FLOWS.CONVERSATION_BANNER.PAUSE')"
            :disabled="busy"
            @click="pauseRun"
          >
            <span class="i-lucide-pause size-3.5 shrink-0" />
            {{ t('TELETEL_FLOWS.CONVERSATION_BANNER.PAUSE') }}
          </button>
          <button
            v-if="run.status === 'paused'"
            type="button"
            :class="MENU_ITEM_CLASS"
            :aria-label="t('TELETEL_FLOWS.ACTIONS.RESUME')"
            :disabled="busy"
            @click="resumeRun"
          >
            <span class="i-lucide-play size-3.5 shrink-0" />
            {{ t('TELETEL_FLOWS.ACTIONS.RESUME') }}
          </button>
          <button
            v-if="!isTerminal"
            type="button"
            :class="MENU_ITEM_CLASS"
            :aria-label="t('TELETEL_FLOWS.ACTIONS.STOP')"
            :disabled="busy"
            @click="stopRun"
          >
            <span class="i-lucide-square size-3.5 shrink-0" />
            {{ t('TELETEL_FLOWS.ACTIONS.STOP') }}
          </button>
          <button
            v-if="run.status === 'failed' && run.retryable"
            type="button"
            :class="MENU_ITEM_CLASS"
            :aria-label="t('TELETEL_FLOWS.CONVERSATION_BANNER.RETRY')"
            :disabled="busy"
            @click="retryRun"
          >
            <span class="i-lucide-refresh-cw size-3.5 shrink-0" />
            {{ t('TELETEL_FLOWS.CONVERSATION_BANNER.RETRY') }}
          </button>
        </template>
        <button
          type="button"
          :class="MENU_ITEM_CLASS"
          :aria-label="t('TELETEL_FLOWS.CONVERSATION_BANNER.DETAILS')"
          :disabled="busy"
          @click="openDetails"
        >
          <span class="i-lucide-info size-3.5 shrink-0" />
          {{ t('TELETEL_FLOWS.CONVERSATION_BANNER.DETAILS') }}
        </button>
        <button
          v-if="isTerminal"
          type="button"
          :class="MENU_ITEM_CLASS"
          :aria-label="t('TELETEL_FLOWS.CONVERSATION_BANNER.START_NEW')"
          :title="
            canAct ? '' : t('TELETEL_FLOWS.CONVERSATION_BANNER.NO_PERMISSION')
          "
          :disabled="busy || !canAct"
          @click="openSelector"
        >
          <span class="i-lucide-play size-3.5 shrink-0" />
          {{ t('TELETEL_FLOWS.CONVERSATION_BANNER.START_NEW') }}
        </button>
      </div>
    </div>

    <!-- flow selector -->
    <div
      v-if="selectorOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-n-alpha-black2"
      @click.self="selectorOpen = false"
    >
      <div
        class="w-96 max-h-[70vh] overflow-auto p-4 rounded-xl bg-n-background border border-n-weak"
      >
        <h3 class="mb-2 text-sm font-medium text-n-slate-12">
          {{ t('TELETEL_FLOWS.CONVERSATION_BANNER.SELECTOR_TITLE') }}
        </h3>
        <input
          v-model="search"
          class="mb-2 text-sm"
          :placeholder="t('TELETEL_FLOWS.CONVERSATION_BANNER.SEARCH')"
        />
        <p v-if="!filteredFlows.length" class="text-sm text-n-slate-11">
          {{ t('TELETEL_FLOWS.CONVERSATION_BANNER.NO_FLOWS') }}
        </p>
        <button
          v-for="flow in filteredFlows"
          :key="flow.id"
          type="button"
          class="flex justify-between w-full px-2 py-1.5 text-sm rounded-lg text-start hover:bg-n-slate-2 text-n-slate-12"
          @click="pickFlow(flow)"
        >
          <span class="truncate">{{ flow.name }}</span>
          <span class="text-n-slate-10">
            {{ t(`TELETEL_FLOWS.TRIGGERS.${flow.trigger_type}`) }}
          </span>
        </button>
      </div>
    </div>

    <!-- confirmation -->
    <div
      v-if="confirmFlow"
      class="fixed inset-0 z-50 flex items-center justify-center bg-n-alpha-black2"
    >
      <div class="p-4 rounded-xl w-96 bg-n-background border border-n-weak">
        <h3 class="mb-2 text-sm font-medium text-n-slate-12">
          {{ t('TELETEL_FLOWS.CONVERSATION_BANNER.CONFIRM_TITLE') }}
        </h3>
        <p class="mb-3 text-sm text-n-slate-11">
          {{
            t('TELETEL_FLOWS.CONVERSATION_BANNER.CONFIRM_TEXT', {
              name: confirmFlow.name,
              trigger: t(`TELETEL_FLOWS.TRIGGERS.${confirmFlow.trigger_type}`),
              nodes: confirmFlow.node_count,
            })
          }}
        </p>
        <div class="flex justify-end gap-2">
          <NextButton slate sm :disabled="busy" @click="confirmFlow = null">
            {{ t('TELETEL_FLOWS.CONVERSATION_BANNER.CANCEL') }}
          </NextButton>
          <NextButton
            sm
            :is-loading="busy"
            :disabled="busy"
            @click="confirmStart"
          >
            {{ t('TELETEL_FLOWS.CONVERSATION_BANNER.CONFIRM_START') }}
          </NextButton>
        </div>
      </div>
    </div>

    <!-- details panel -->
    <div
      v-if="detailsOpen && details"
      class="fixed inset-0 z-50 flex items-center justify-center bg-n-alpha-black2"
      @click.self="detailsOpen = false"
    >
      <div
        class="w-[28rem] max-h-[75vh] overflow-auto p-4 rounded-xl bg-n-background border border-n-weak text-sm"
      >
        <h3 class="mb-2 font-medium text-n-slate-12">
          {{ t('TELETEL_FLOWS.CONVERSATION_BANNER.DETAILS_TITLE') }}
        </h3>
        <dl class="grid grid-cols-2 gap-1 mb-3 text-n-slate-11">
          <dt>{{ t('TELETEL_FLOWS.CONVERSATION_BANNER.FIELD_FLOW') }}</dt>
          <dd class="text-n-slate-12">{{ details.flow_name }}</dd>
          <dt>{{ t('TELETEL_FLOWS.CONVERSATION_BANNER.FIELD_STATUS') }}</dt>
          <dd class="text-n-slate-12">
            {{ t(`TELETEL_FLOWS.STATUS.${details.status}`) }}
          </dd>
          <dt>{{ t('TELETEL_FLOWS.CONVERSATION_BANNER.FIELD_NODE') }}</dt>
          <dd class="text-n-slate-12">{{ details.current_node_id }}</dd>
          <dt>{{ t('TELETEL_FLOWS.CONVERSATION_BANNER.FIELD_STARTED_AT') }}</dt>
          <dd class="text-n-slate-12">{{ formatTime(details.created_at) }}</dd>
          <dt>{{ t('TELETEL_FLOWS.CONVERSATION_BANNER.FIELD_STARTED_BY') }}</dt>
          <dd class="text-n-slate-12">{{ details.started_by }}</dd>
          <template v-if="details.failure_reason">
            <dt>{{ t('TELETEL_FLOWS.CONVERSATION_BANNER.FIELD_REASON') }}</dt>
            <dd class="text-n-ruby-11">{{ details.failure_reason }}</dd>
          </template>
        </dl>
        <p class="mb-1 font-medium text-n-slate-11">
          {{ t('TELETEL_FLOWS.CONVERSATION_BANNER.FIELD_EVENTS') }}
        </p>
        <ul class="flex flex-col gap-0.5 text-xs text-n-slate-11">
          <li v-for="event in details.events || []" :key="event.id">
            {{ formatTime(event.created_at) }} · {{ event.event_type }}
            <template v-if="event.node_id"> · {{ event.node_id }}</template>
            <template v-if="event.payload?.branch">
              · {{ event.payload.branch }}
            </template>
            <template v-if="event.delivery_status">
              · {{ event.delivery_status }}
            </template>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
